import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { centerClient } from '../../shared/clients';
import { Button, ButtonGroup } from 'react-bootstrap';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { agTextColumnFilterProps, SelectFilter } from '../../utils/AgGrid/agGridTheme';
import { AG_GRID_LOCALE_RU } from 'blick.react.aggrid';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { confirm } from '../../utils/confirm';
import { buildDeviceId } from '../../utils/deviceId';
import { BlickAgGrid } from 'blick.react.aggrid';

interface IDataInFutureTableRow {
    model: number;
    modelName?: string;
    serialNumber?: string;
    account: string;
    client: string;
    lastDataTime?: string;
}
const DataInFuture = () => {
    const [data, setData] = useState<IDataInFutureTableRow[]>([]);
    const tableRef = useRef<AgGridReact<IDataInFutureTableRow>>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);

    const deleteFutureData = async () => {
        if (tableRef?.current?.api.getSelectedRows().length == 0)
            return toast.warn("Выберите хотя бы один контроллер");

        const deviceIds = tableRef?.current?.api.getSelectedRows().map(device => {
            if (device.serialNumber)
                return buildDeviceId(device.model, device.serialNumber);
        }).filter(id => id !== undefined);

        const confirmRemove = confirm({
            confirmation: "Подтверждение удаления данных" ,
            options: {
                body: "Вы действительно желаете удалить данные в будущем для выбранных устройств?\n" +
                    "Убедитесь, что время на Вашем компьютере не расходится с истинным."
            }
        });
        if (await confirmRemove) {
            centerClient.deleteDataInFuture(deviceIds ?? [])
                .then(() => {
                    toast.success("Данные в будущем успешно удалены");
                    getDevices();
                })
                .catch(() => void 0);
        }
    }

    const getDevices = () => {
        setIsLoading(true);
        centerClient.getAllDevices()
            .then((res) => {
                const data = res.data;
                if (!data)
                    return;
                const newData: IDataInFutureTableRow[] = [];
                Object.entries(data).forEach(([client, accounts]) => {
                    Object.entries(accounts).forEach(([account, devices]) => {
                        devices.forEach(device => {
                            const lastDataTime = dayjs(new Date((device.LastDataTime?.Seconds ?? 0) * 1000));
                            const futureDate = dayjs().add(30, 'minutes');
                            if (lastDataTime > futureDate)
                                newData.push({
                                    model: device.Model,
                                    modelName: device.DeviceModelName,
                                    serialNumber: device.Serial,
                                    account: account,
                                    client: client,
                                    lastDataTime: lastDataTime.toLocaleString()
                                })
                        });
                    })
                })
                setData(newData);
            })
            .catch(() => void 0)
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        getDevices();
    }, []);

    const modelOptions = useMemo(() => {
        const set = new Set<string>();
        data.forEach(d => {
            if (d.modelName) set.add(d.modelName);
        });
        return Array.from(set);
    }, [data]);

    const clientsOptions = useMemo(() => {
        const set = new Set<string>();
        data.forEach(d => {
            if (d.client) set.add(d.client);
        });
        return Array.from(set);
    }, [data]);

    const accountsOptions = useMemo(() => {
        const set = new Set<string>();
        data.forEach(d => {
            if (d.account) set.add(d.account);
        });
        return Array.from(set);
    }, [data]);

    const columns = useMemo<ColDef<IDataInFutureTableRow>[]>(() => [
        {
            headerName: "№",
            maxWidth: 60,
            editable: false,
            valueGetter: (props) => {
                return props.node!.rowIndex! + 1;
            }
        },
        {
            field: 'client',
            headerName: "Клиент",
            editable: false,
            floatingFilterComponentParams: { options: clientsOptions },
            ...SelectFilter
        },
        {
            field: 'account',
            headerName: "Аккаунт",
            editable: false,
            floatingFilterComponentParams: { options: accountsOptions },
            ...SelectFilter
        },
        {
            field: 'modelName',
            headerName: "Модель",
            floatingFilterComponentParams: { options: modelOptions },
            width: 125,
            minWidth: 125,
            editable: false,
            ...SelectFilter
        },
        {
            field: 'serialNumber',
            headerName: "с.н",
            editable: false,
            minWidth: 60,
            maxWidth: 100,
            ...agTextColumnFilterProps(),
        },
        {
            field: 'lastDataTime',
            headerName: "Время посл. данных",
            editable: false,
        },
    ], [modelOptions, accountsOptions, clientsOptions]);

    const sizeToFit = useCallback(() => {
        // AG Grid #29: не вызываем sizeColumnsToFit, если контейнер ещё не виден (нулевая ширина)
        const el = tableContainerRef.current;
        if (!el || el.clientWidth === 0)
            return;
        setTimeout(() => {
            tableRef.current?.api?.sizeColumnsToFit({
                defaultMinWidth: 50,
            });
        }, 0)
    }, []);

    useLayoutEffect(() => {
        // Вызов при монтировании и при resize
        window.addEventListener("resize", sizeToFit);

        // Ресайз колонок при первом показе подтаба (контейнер меняет ширину с 0) и resize контейнера
        const el = tableContainerRef.current;
        let observer: ResizeObserver | null = null;
        if (el) {
            observer = new ResizeObserver(() => {
                sizeToFit();
            });
            observer.observe(el);
        }

        return () => {
            window.removeEventListener("resize", sizeToFit);
            observer?.disconnect();
        };
    }, [sizeToFit]);

    return (
        <div style={{ height: '100%', width: '60rem' }}>
            <ButtonGroup style={{ padding:'1rem' }}>
                <Button onClick={getDevices} variant="custom"><b>Обновить</b></Button>
                <Button onClick={deleteFutureData} variant="custom"><b>Удалить все данные в будущем</b></Button>
            </ButtonGroup>
            <div ref={tableContainerRef} style={{ height: `calc(100% - 5rem)` }}>
                <BlickAgGrid
                    showRestoreColumnsButton={true}
                    loading={isLoading}
                    rowData={data}
                    ref={tableRef}
                    //theme={myAgReactTheme}
                    localeText={AG_GRID_LOCALE_RU}
                    columnDefs={columns}
                    onRowDataUpdated={sizeToFit}
                    selectionColumnDef={{ maxWidth: 50 }}
                    rowSelection={{
                        mode: 'multiRow',
                        checkboxes: true
                    }}
                    onModelUpdated={(e) => { e.api.refreshCells() }}
                />
            </div>
        </div>
    )
};

export default DataInFuture;
